import {
  Button,
  SettingsPage as ParcelSettingsPage,
  SettingsSection,
  SudoModeProvider,
} from '@abodeminehq/parcel-ui'
import { AccountPage } from '@abodeminehq/entitlements-client-react/ui'
import { faClose } from '@abodeminehq/parcel-ui/icons/fa/pro-regular'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Navigate, useNavigate, useRouter } from '@tanstack/react-router'
import { useAuthContext } from '~/contexts/auth-context'
import { runtimeConfig } from '~/lib/runtime-config'

/**
 * Settings tabs are driven by the `/settings/$tab` template URL — the tab
 * segment is the source of truth for the selected section. Only the user
 * section exists for now; add ids here as new sections land.
 */
export const SETTINGS_TABS = ['user'] as const

export type SettingsTab = (typeof SETTINGS_TABS)[number]

export const DEFAULT_SETTINGS_TAB: SettingsTab = 'user'

export function isSettingsTab(tab: string): tab is SettingsTab {
  return (SETTINGS_TABS as readonly string[]).includes(tab)
}

export function SettingsPage({ tab }: { tab: string }) {
  const navigate = useNavigate()
  const router = useRouter()
  const { isAuthenticated, isLoading } = useAuthContext()

  const handleBack = () => {
    if (router.history.canGoBack()) {
      router.history.back()
      return
    }
    navigate({ to: '/' })
  }

  if (isLoading) {
    return (
      <div className="flex h-svh items-center justify-center">
        <div>Loading settings...</div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />
  }

  if (!isSettingsTab(tab)) {
    return (
      <Navigate
        to="/settings/$tab"
        params={{ tab: DEFAULT_SETTINGS_TAB }}
        replace
      />
    )
  }

  return (
    <SudoModeProvider>
      <ParcelSettingsPage
        headerActions={
          <Button
            className="mr-4"
            variant="ghost"
            size="sm"
            onClick={handleBack}
          >
            <FontAwesomeIcon icon={faClose} className="size-4" />
          </Button>
        }
        sectionId={tab}
        onSectionChanged={(section) =>
          navigate({ to: '/settings/$tab', params: { tab: section.id } })
        }
      >
        <SettingsSection id="user" name="User">
          <AccountPage logtoEndpoint={runtimeConfig.VITE_LOGTO_ENDPOINT} />
        </SettingsSection>
      </ParcelSettingsPage>
    </SudoModeProvider>
  )
}
